import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { extractList, extractPagination } from '../utils/response';
import { getErrorMessage } from '../utils/errorHandler';
import {
  useGetIssuesQuery,
  useLazyGetUsersListQuery,
  useLazyGetLeadsStatusQuery,
} from '../store/api';
import { useLazySelectOptions } from './useLazySelectOptions';

const buildQueryParams = (page, pageSize, search, filters, sort) => {
  const params = {
    // Rows render the last log + lead only; full issueLogs/model/author are detail-page data
    'relations[]': ['lastIssueLog', 'lead'],
    paginate: 1,
    page: page + 1,
    per_page: pageSize,
    ...(search && { search }),
  };
  // `status_id` (lives on issue_logs) and `title` (partial match) are handled by
  // IssueRepository::applyCustomFilters, so they go top-level — the generic
  // filters[] path would run them as an exact WHERE on the issues table.
  const TOP_LEVEL_FILTERS = ['status_id', 'title'];
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    if (TOP_LEVEL_FILTERS.includes(key)) {
      params[key] = value;
    } else {
      params[`filters[${key}]`] = value;
    }
  });
  if (sort?.sort_by) params.sort_by = sort.sort_by;
  if (sort?.sort_direction) params.sort_direction = sort.sort_direction;
  return params;
};

export const useIssues = (canViewListIssue = true) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    title: '',
    status_id: '',
    author_id: '',
    model_type: '',
  });
  const [sort, setSort] = useState({
    sort_by: 'created_at',
    sort_direction: 'desc',
  });
  const [processingId, setProcessingId] = useState(null);
  const [localIssues, setLocalIssues] = useState(null);

  useEffect(() => {
    if (!canViewListIssue) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPagination((p) => ({ ...p, page: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchValue, canViewListIssue]);

  const queryParams = useMemo(
    () =>
      buildQueryParams(
        pagination.page,
        pagination.pageSize,
        debouncedSearch,
        filters,
        sort
      ),
    [pagination.page, pagination.pageSize, debouncedSearch, filters, sort]
  );

  const {
    data: issuesData,
    isLoading,
    isFetching,
    error: issuesError,
    refetch,
  } = useGetIssuesQuery(queryParams, { skip: !canViewListIssue });

  const [fetchStatuses] = useLazyGetLeadsStatusQuery();
  const [fetchUsers] = useLazyGetUsersListQuery();

  const statusSelect = useLazySelectOptions(fetchStatuses, {
    args: { 'conditions[model]': 'Issue' },
    enabled: canViewListIssue,
  });

  const usersSelect = useLazySelectOptions(fetchUsers, {
    args: {},
    mapResult: (res) => res?.list ?? extractList(res),
    enabled: canViewListIssue,
  });

  useEffect(() => {
    if (!canViewListIssue) {
      toast.error('You do not have permission to view issues.');
    }
  }, [canViewListIssue]);

  useEffect(() => {
    if (issuesError) {
      toast.error(getErrorMessage(issuesError) || 'Failed to fetch issues');
    }
  }, [issuesError]);

  useEffect(() => {
    if (issuesData) {
      setLocalIssues(null);
      const pag = extractPagination(issuesData);
      const nextTotal = pag.total ?? 0;
      setPagination((p) =>
        pag.total == null || p.total === nextTotal
          ? p
          : { ...p, total: nextTotal }
      );
    }
  }, [issuesData]);

  const issues = localIssues ?? extractList(issuesData);
  const tableLoading = isLoading || isFetching;

  const fetchIssues = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const handleSortChange = (field) => {
    setSort((prev) => ({
      sort_by: field,
      sort_direction:
        prev.sort_by === field && prev.sort_direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const clearFilters = () => {
    setFilters({ title: '', status_id: '', author_id: '', model_type: '' });
    setSort({ sort_by: 'created_at', sort_direction: 'desc' });
  };

  const handleSearchChange = (e) => setSearchValue(e.target.value);

  const handleClearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const handlePageChange = (_, newPage) =>
    setPagination((p) => ({ ...p, page: newPage }));

  const handleRowsPerPageChange = (e) => {
    const sz = parseInt(e.target.value, 10);
    setPagination((p) => ({ ...p, page: 0, pageSize: sz }));
  };

  const setIssues = (updater) => {
    if (typeof updater === 'function') {
      setLocalIssues((prev) => {
        const current = prev ?? extractList(issuesData);
        return updater(current);
      });
    }
  };

  const getActiveFiltersCount = () =>
    Object.values(filters).filter(
      (v) => v !== '' && v !== null && v !== undefined
    ).length;

  return {
    issues,
    tableLoading,
    searchValue,
    pagination,
    filters,
    sort,
    usersList: usersSelect.options,
    usersListLoading: usersSelect.loading,
    loadUsersList: usersSelect.load,
    statusOptions: statusSelect.options,
    statusOptionsLoading: statusSelect.loading,
    loadStatusOptions: statusSelect.load,
    processingId,
    setProcessingId,
    setIssues,
    fetchIssues,
    handleSearchChange,
    handleClearSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    handleSortChange,
    clearFilters,
    getActiveFiltersCount,
  };
};
